import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language } from '../types';
import { translations, languageOptions, t as translate } from '../i18n/translations';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
  dir: 'ltr' | 'rtl';
  languageOptions: typeof languageOptions;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>('en');
  
  // Load language preference from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('ocb_language') as Language;
    if (stored && Object.keys(translations).includes(stored)) {
      setLanguageState(stored);
    } else {
      // Detect browser language
      const browserLang = navigator.language.split('-')[0] as Language;
      if (Object.keys(translations).includes(browserLang)) {
        setLanguageState(browserLang);
      }
    }
  }, []);

  // Update document direction and save preference
  useEffect(() => {
    const langOption = languageOptions.find(l => l.code === language);
    document.documentElement.dir = langOption?.dir || 'ltr';
    document.documentElement.lang = language;
    localStorage.setItem('ocb_language', language);
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    return translate(key, language);
  };

  const dir = languageOptions.find(l => l.code === language)?.dir === 'rtl' ? 'rtl' : 'ltr';

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, dir, languageOptions }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
